import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import {
  withScriptjs,
  withGoogleMap,
  GoogleMap,
  Marker,
  OverlayView,
} from 'react-google-maps';
import { MarkerClusterer } from 'react-google-maps/lib/components/addons/MarkerClusterer';

import InfoWindow from './InfoWindow';
import Label from './Label';


import {
  GOOGLE_API_ORIGIN,
  GOOGLE_API_MAP_PATH,
  GOOGLE_API_MAP_QUERY,
  GOOGLE_API_MAP_ZOOM_DEFAULT,
  GOOGLE_API_MAP_CENTER,
  GOOGLE_DEFAULT_OPTIONS,
  GOOGLE_DEFAULT_PIN,
  GOOGLE_MARKER_SIZE,
  GOOGLE_MARKER_TOP_MARGIN,
  GOOGLE_INFO_WINDOW_MARGIN,
  GOOGLE_CLUSTER_SIZES,
  GOOGLE_MIN_CLUSTER_SIZE,
} from '../../../constants/google-maps';

import Debouncer from '../../../util/debouncer';
import ViewManager from '../../../managers/view';
import createCluster from '../SiteSeeker/helpers/createCluster';


const AsyncGoogleMap = withScriptjs(withGoogleMap(props => (
  <GoogleMap
    options={{
      ...GOOGLE_DEFAULT_OPTIONS,
      ...props.options,
      fullscreenControl: props.showFullScreenButton,
    }}
    onProjectionChanged={props.onProjectionChanged}
    ref={props.handleMapCreated}
    defaultCenter={props.defaultCenter}
    defaultZoom={props.defaultZoom || GOOGLE_API_MAP_ZOOM_DEFAULT}
  >
    <MarkerClusterer
      enableRetinaIcons
      minimumClusterSize={props.minimumClusterSize || GOOGLE_MIN_CLUSTER_SIZE}
      maxZoom={18}
      gridSize={84}
      styles={GOOGLE_CLUSTER_SIZES.map(size => ({
        textColor: 'white',
        height: size,
        url: createCluster(size),
        width: size,
      }))}
      onClusteringEnd={props.onClusteringEnd}
    >
      {props.children}
    </MarkerClusterer>
  </GoogleMap>
)));


export default class Map extends Component {
  static propTypes = {
    apiKey: PropTypes.string.isRequired,
    defaultZoom: PropTypes.number,
    mapStyles: PropTypes.array,
    minimumClusterSize: PropTypes.number,
    ui: PropTypes.element,
    selectedIndex: PropTypes.number,
    results: PropTypes.arrayOf(
      PropTypes.shape({
        latitude: PropTypes.number.isRequired,
        longitude: PropTypes.number.isRequired,
        overLabelContent: PropTypes.element,
        selectedInfoWindowContent: PropTypes.func,
        iconSVGString: PropTypes.String,
        iconObject: PropTypes.object,
      }),

    ).isRequired,
    onMarkerSelect: PropTypes.func,
    showFullScreenButton: PropTypes.bool.isRequired,
  };


  static defaultProps = {
    onMarkerOver: () => {},
    selectedIndex: -1,
  };


  constructor(props) {
    super(props);
    this.selectedMarker = (props.selectedIndex !== undefined)
      ? props.results[props.selectedIndex]
      : null;
    this.selectedMarkerInCluster = false;
    this.overMarker = null;
    this.viewDebouncer = null;
    this.googleMap = null;
    this.ignoreInitialZoomChange = (props.results.length > 1);
  }


  /**
   * Looking to refactor for Async Info Windows to be a thing, until that happens this public
   * available method is used to update the component if an Info Windows content as been
   * update asynchronously.
   *
   * @public
   */
  updateAfterSelectedInfoWindowContentLoaded(cb) {
    // reactivating this allows the InfoWindow to be paned into view upon update
    this.hasMarkerClicked = true;
    this.forceUpdate(cb); // on render the InfoWindow is repositioned correctly.
  }


  componentDidUpdate(prevProps) {
    const previousResultsLatLng = Map.serializeResultsLatLng(prevProps.results);
    if (previousResultsLatLng !== Map.serializeResultsLatLng(this.props.results)) {
      this.fitBounds();
    }
  }


  static serializeResultsLatLng(results) {
    return results.reduce((str, result) => `${result.latitude}${result.longitude}`, '');
  }


  componentDidMount() {
    // Establish a debouncer to determine when scrolling stops
    this.viewDebouncer = new Debouncer(300, true, this.onViewResize);

    // debounce resize event, as this also updates the view
    ViewManager.on(ViewManager.EVENT_RESIZE, this.onViewResizeDebounce);
  }


  componentWillUnmount() {
    ViewManager.off(ViewManager.EVENT_RESIZE, this.onViewResizeDebounce);
    this.viewDebouncer.destroy();
  }


  onViewResizeDebounce = () => {
    this.viewDebouncer.debounce();
  }


  onViewResize = () => {
    this.forceUpdate();
  }


  handleMapCreated = (googleMap) => {
    if (this.googleMap) {
      return;
    }

    this.googleMap = googleMap;

    if (this.props.onCreated) {
      this.props.onCreated();
    }

    this.fitBounds();
    this.forceUpdate();
  }


  handleProjectionChanged = () => {
    this.fitBounds();
  }


  handleClusteringEnd = (markerCluster) => {
    let cluster;
    let marker;
    let latMatch;
    let lngMatch;
    if (!this.selectedMarker) return;

    // eslint-disable-next-line
    const clusters = markerCluster.clusters_; // I'm having to inspect custer object

    for (let c = 0; c < clusters.length; c += 1) {
      cluster = clusters[c];

      // eslint-disable-next-line
      const markers = cluster.markers_; // I'm having to inspect custer object

      for (let m = 0; m < markers.length; m += 1) {
        marker = markers[m];

        latMatch =
          parseFloat(this.selectedMarker.latitude.toFixed(6))
            === parseFloat(marker.position.lat().toFixed(6));
        lngMatch =
          parseFloat(this.selectedMarker.longitude.toFixed(6))
            === parseFloat(marker.position.lng().toFixed(6));

        if (latMatch && lngMatch) {
          const selectedMarkerInCluster = (markers.length >= (
            this.props.minimumClusterSize || GOOGLE_MIN_CLUSTER_SIZE
          ));

          if (selectedMarkerInCluster !== this.selectedMarkerInCluster) {
            this.selectedMarkerInCluster = selectedMarkerInCluster;

            this.forceUpdate();
          }
        }
      }
    }
  }


  handleMarkerOver = (markerData) => {
    const overMarker = this.props.results[markerData.key];

    if (overMarker !== this.overMarker && overMarker.overLabelContent) {
      this.overMarker = overMarker;
      this.forceUpdate();
    }
  }


  handleMarkerOut = (markerData) => {
    const overMarker = this.props.results[markerData.key];

    if (this.canIShowOver(overMarker)) {
      this.overMarker = null;
      this.forceUpdate();
    }
  }


  handleInfoWindowClose = () => {
    if (this.selectedMarker == null) return;

    this.selectedMarker = null;
    this.overMarker = null;
    this.forceUpdate();
  }


  handleMarkerClick = (markerData) => {
    const selectedMarker = this.props.results[markerData.key];
    if (selectedMarker !== this.selectedMarker) {
      this.selectedMarker = selectedMarker;
      this.hasMarkerClicked = true;

      if (this.props.onMarkerSelect) this.props.onMarkerSelect(markerData);

      this.googleMapContainer.scrollIntoView(false); // remainder if needed

      this.forceUpdate();
    }
  }


  handleCreateInfoWindow = (ref) => {
    if (ref === null) return;

    this.infoWindowRef = ref;

    if (this.hasMarkerClicked) {
      this.hasMarkerClicked = false;
      this.checkToPanToMarkerPosition();
    }
  }


  checkToPanToMarkerPosition() {
    const markerLatLng = new google.maps.LatLng({
      lat: this.selectedMarker.latitude,
      lng: this.selectedMarker.longitude,
    });

    const mapWidth = this.googleMapContainer.clientWidth;
    const mapHeight = this.googleMapContainer.clientHeight;
    let markerOffsetLatLng;
    const defaultPanToPixelPoint = new google.maps.Point(mapWidth / 2, mapHeight / 2);

    const markerPoint = this.getPixelPointFromLatLng(markerLatLng);

    const markerAllowance = GOOGLE_MARKER_SIZE
      + GOOGLE_MARKER_TOP_MARGIN + GOOGLE_INFO_WINDOW_MARGIN;

    const infoWindowHeight = this.infoWindowRef.clientHeight;
    const infoWindowWidth = this.infoWindowRef.clientWidth;

    const heightIsOk = Map.canIShowInfoWindowWithinHeight(
      markerPoint,
      mapHeight,
      infoWindowHeight,
      markerAllowance,
    );

    const widthIsOk = Map.canIShowInfoWindowWithinWidth(markerPoint, mapWidth, infoWindowWidth);

    if (!widthIsOk || !heightIsOk) {
      if (defaultPanToPixelPoint.y - markerAllowance - infoWindowHeight < 0) {
        const newSafeDestinationPixelPoint = new google.maps.Point(
          mapWidth / 2,
          markerAllowance + infoWindowHeight,
        );

        markerOffsetLatLng = this.getOffsetLatLng(
          mapWidth,
          mapHeight,
          newSafeDestinationPixelPoint,
          markerLatLng,
        );

        this.googleMap.panTo(markerOffsetLatLng);
      } else {
        this.googleMap.panTo(markerLatLng);
      }
    }
  }


  static canIShowInfoWindowWithinHeight(point, mapheight, infoWindowHeight, margin) {
    return point.y - (infoWindowHeight + margin) >= 0 && point.y < mapheight;
  }


  static canIShowInfoWindowWithinWidth(point, mapWidth, infoWindowWidth) {
    return point.x - (infoWindowWidth / 2) >= 0 && point.x + (infoWindowWidth / 2) < mapWidth;
  }


  canIShowOver(marker) {
    return marker === this.overMarker
      && marker.overLabelContent
      && (this.selectedMarker !== marker || !marker.selectedInfoWindowContent);
  }


  getOffsetLatLng(mapWidth, mapHeight, destinationPixelPoint, selectedLatLng) {
    const defaultPanToPixelPoint = new google.maps.Point(mapWidth / 2, mapHeight / 2);
    const destinationLatLong = this.getLatLngFromPixelPoint(destinationPixelPoint);
    const centreLatLong = this.getLatLngFromPixelPoint(defaultPanToPixelPoint);
    const offsetLat = centreLatLong.lat() - destinationLatLong.lat();
    const offsetLng = centreLatLong.lng() - destinationLatLong.lng();
    const offsettedLat = selectedLatLng.lat() + offsetLat;
    const offsettedLng = selectedLatLng.lng() + offsetLng;
    const markerOffsetLatLng = new google.maps.LatLng(offsettedLat, offsettedLng);
    return markerOffsetLatLng;
  }


  getPixelPointFromLatLng(latLng) {
    const projection = this.googleMap.getProjection();
    const bounds = this.googleMap.getBounds();
    const scale = 2 ** this.googleMap.getZoom();
    const bottomLeft = projection.fromLatLngToPoint(bounds.getSouthWest());
    const topRight = projection.fromLatLngToPoint(bounds.getNorthEast());
    const worldPoint = projection.fromLatLngToPoint(latLng);

    return new google.maps.Point(
      Math.floor((worldPoint.x - bottomLeft.x) * scale),
      Math.floor((worldPoint.y - topRight.y) * scale),
    );
  }


  getLatLngFromPixelPoint(pixelPoint) {
    const projection = this.googleMap.getProjection();
    const bounds = this.googleMap.getBounds();
    const scale = 2 ** this.googleMap.getZoom();
    const bottomLeft = projection.fromLatLngToPoint(bounds.getSouthWest());
    const topRight = projection.fromLatLngToPoint(bounds.getNorthEast());
    const worldX = bottomLeft.x + (pixelPoint.x / scale);
    const worldY = topRight.y + (pixelPoint.y / scale);
    const worldPoint = new google.maps.Point(worldX, worldY);
    return projection.fromPointToLatLng(worldPoint);
  }


  getInfoWindowPixelPositionOffset = (width, height) => (
    {
      x: -(width / 2),
      y: -height - GOOGLE_MARKER_SIZE - GOOGLE_MARKER_TOP_MARGIN,
    }
  );


  getLabelWindowPixelPositionOffset = (width, height) => (
    {
      x: -(width / 2),
      y: -height - GOOGLE_MARKER_SIZE - GOOGLE_MARKER_TOP_MARGIN,
    }
  );


  static getPinIcon(result, isActive) {
    const unselectedScale = 0.8;
    const scale = isActive ? GOOGLE_MARKER_SIZE / 100 : GOOGLE_MARKER_SIZE * unselectedScale / 100;

    if (result.iconSVGString) {
      const activeSize = new google.maps.Size(GOOGLE_MARKER_SIZE, GOOGLE_MARKER_SIZE);
      const inactiveSize = new google.maps.Size(
        GOOGLE_MARKER_SIZE * unselectedScale,
        GOOGLE_MARKER_SIZE * unselectedScale,
      );

      return {
        url: result.iconSVGString,
        scaledSize: isActive ? activeSize : inactiveSize,
      };
    } else if (result.iconObject && result.iconObject.constructor === Object) {
      return {
        ...result.iconObject,
        scale,
      };
    }

    return {
      ...GOOGLE_DEFAULT_PIN,
      scale,
    };
  }


  getBoundsForZoomAndPosition(zoom, center) {
    const projection = this.googleMap.getProjection();
    if (!projection) return new google.maps.LatLngBounds();
    const zoomFactor = (2 ** zoom) * 2;
    const mapWidth = this.googleMapContainer.clientWidth / zoomFactor;
    const mapHeight = this.googleMapContainer.clientHeight / zoomFactor;
    const centerPoint = projection.fromLatLngToPoint(center);
    const southWest = new google.maps.Point(centerPoint.x - mapWidth, centerPoint.y + mapHeight);
    const northEast = new google.maps.Point(centerPoint.x + mapWidth, centerPoint.y - mapHeight);

    return new google.maps.LatLngBounds(
      projection.fromPointToLatLng(southWest),
      projection.fromPointToLatLng(northEast),
    );
  }


  fitBounds() {
    if (!window.google) return;

    const projection = this.googleMap.getProjection();

    if (!this.googleMap || !projection) return;
    if (this.props.selectedIndex > -1) return;

    let bounds;

    const { results } = this.props;

    if (results.length > 1) {
      bounds = new google.maps.LatLngBounds();
      results.forEach((result) => {
        bounds.extend(
          new google.maps.LatLng(result.latitude, result.longitude),
        );
      });
      this.googleMap.fitBounds(bounds);
    } else if (results.length === 1) {
      const defaultZoom = this.props.defaultZoom ?
        this.props.defaultZoom : GOOGLE_API_MAP_ZOOM_DEFAULT;
      const center = new google.maps.LatLng(results[0].latitude, results[0].longitude);
      bounds = this.getBoundsForZoomAndPosition(defaultZoom, center);
      this.googleMap.fitBounds(bounds);
    }
  }


  render() {
    let defaultResult;
    let markers = [];
    let defaultCenter = GOOGLE_API_MAP_CENTER;

    const containerClasses = classNames({
      Map_containerElement: true,
      'Map_containerElement-loaded': this.googleMap !== undefined,
    });

    // reestablish default center
    if (this.props.selectedIndex !== undefined && this.props.selectedIndex > -1) {
      defaultResult = this.props.results[this.props.selectedIndex];
      defaultCenter = { lat: defaultResult.latitude, lng: defaultResult.longitude };
    } else if (this.props.results.length) {
      defaultResult = this.props.results[0];
      defaultCenter = { lat: defaultResult.latitude, lng: defaultResult.longitude };
    }

    if (window.google) {
      markers = this.props.results.map((result, index) => {
        const position = { lat: result.latitude, lng: result.longitude };
        const isSelected = this.props.results.indexOf(this.selectedMarker) === index;
        const canIShowInfoWindow = isSelected
          && result.selectedInfoWindowContent
          && !this.selectedMarkerInCluster;
        const canIShowOver = this.canIShowOver(result);

        const markerProps = {
          position,
          key: index,
          icon: Map.getPinIcon(result, isSelected),
        };

        return (
          <Marker
            key={markerProps.key}
            {...markerProps}
            onClick={() => this.handleMarkerClick(markerProps)}
            onMouseOver={() => this.handleMarkerOver(markerProps)}
            onMouseOut={() => this.handleMarkerOut(markerProps)}
          >
            {canIShowInfoWindow
              && <OverlayView
                position={{ lat: markerProps.position.lat, lng: markerProps.position.lng }}
                mapPaneName={OverlayView.FLOAT_PANE}
                getPixelPositionOffset={this.getInfoWindowPixelPositionOffset}
              >
                <InfoWindow
                  onCloseClick={() => this.handleInfoWindowClose()}
                  getRef={ref => this.handleCreateInfoWindow(ref)}
                >
                  {result.selectedInfoWindowContent(result)}
                </InfoWindow>
              </OverlayView>
            }
            {canIShowOver
              && <OverlayView
                position={{ lat: markerProps.position.lat, lng: markerProps.position.lng }}
                mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                getPixelPositionOffset={this.getLabelWindowPixelPositionOffset}
              >
                <Label>
                  {result.overLabelContent}
                </Label>
              </OverlayView>
            }
          </Marker>
        );
      });
    }

    return (
      <Fragment>
        <AsyncGoogleMap
          googleMapURL={`${GOOGLE_API_ORIGIN}${GOOGLE_API_MAP_PATH}${GOOGLE_API_MAP_QUERY.replace('{0}', this.props.apiKey)}`}

          options={this.props.options}
          loadingElement={
            <div></div>
          }
          containerElement={
            <figure ref={(ref) => {
              this.googleMapContainer = ref;
            }} className={containerClasses} />
          }
          mapElement={
            <div className="Map_element" />
          }
          handleMapCreated={this.handleMapCreated}
          markers={markers}
          minimumClusterSize={this.props.minimumClusterSize}
          onProjectionChanged={this.handleProjectionChanged}
          defaultZoom={this.props.defaultZoom}
          defaultCenter={defaultCenter}
          onClusteringEnd={this.handleClusteringEnd}
          showFullScreenButton={this.props.showFullScreenButton}
        >
          {markers}
        </AsyncGoogleMap>
        <div className="Map_overlay">
          {this.props.ui && this.props.ui}
        </div>
      </Fragment>
    );
  }
}
